import * as React from 'react'

import {
  IMMEDIATE,
  STAGGERED,
  baseDiagnosticImageSrc,
} from './shared'

import {
  Tooltip,
  infoIcon,
} from '../../../../../Shared/index'
import useModalAccessibility from '../../../../../Shared/hooks/useModalAccessibility'
import DemoGuardedButton from '../../../shared/demoGuardedButton'

const baseStaggeredReleaseSrc = `${baseDiagnosticImageSrc}/staggered_release`
const staggeredReleaseIllustrationGray = <img alt="" className="illustration" src={`${baseStaggeredReleaseSrc}/staggered_release_illustration_gray.svg`} />
const staggeredReleaseIllustrationGreen = <img alt="" className="illustration" src={`${baseStaggeredReleaseSrc}/staggered_release_illustration_green.svg`} />
const immediateReleaseIllustrationGray = <img alt="" className="illustration" src={`${baseStaggeredReleaseSrc}/immediate_release_illustration_gray.svg`} />
const immediateReleaseIllustrationGreen = <img alt="" className="illustration" src={`${baseStaggeredReleaseSrc}/immediate_release_illustration_green.svg`} />

type ReleaseMethodOptions = 'staggered'|'immediate'

interface ReleaseMethodModalProps {
  visible: boolean,
  handleClickAssign: () => {},
  handleClickCancel: () => {},
  setReleaseMethod: (releaseMethod: ReleaseMethodOptions) => {},
  releaseMethod?: ReleaseMethodOptions,
  originalReleaseMethod?: ReleaseMethodOptions
}

const ReleaseMethodOption = ({ isSelected, illustration, tooltipText, headerText, subheaderText, descriptionText, onClick, }) => {
  return (
    <button className={`interactive-wrapper focus-on-light release-method-option ${isSelected ? 'selected' : ''}`} onClick={onClick} type="button">
      {illustration}
      <h2>
        <span>{headerText}</span>
        <Tooltip
          isTabbable={false}
          tooltipText={tooltipText}
          tooltipTriggerText={<img alt={infoIcon.alt} src={infoIcon.src} />}
        />
      </h2>
      <p className="subheader">{subheaderText}</p>
      <p className="description">{descriptionText}</p>
    </button>
  )
}

const ReleaseMethodModal = ({ visible, handleClickAssign, handleClickCancel, setReleaseMethod, releaseMethod, originalReleaseMethod, }: ReleaseMethodModalProps) => {
  const { modalRef } = useModalAccessibility(close);
  function setReleaseMethodToStaggered() { setReleaseMethod(STAGGERED)}
  function setReleaseMethodToImmediate() { setReleaseMethod(IMMEDIATE)}

  if (!visible) { return <span /> }

  return (
    <div className="modal-container release-method-modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="releaseMethodModalTitle"
        aria-modal="true"
        className="release-method-modal quill-modal modal-body"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >
        <h1 id="releaseMethodModalTitle">How would you like to release activity packs to students?</h1>
        <div className="release-method-options">
          <ReleaseMethodOption
            descriptionText="Rather than assign all of the activity packs up front, you can choose to unlock only one activity pack at a time. Once students complete that pack, Quill will unlock the next activity pack in the sequence."
            headerText="Staggered Release"
            illustration={releaseMethod === STAGGERED ? staggeredReleaseIllustrationGreen : staggeredReleaseIllustrationGray}
            isSelected={releaseMethod === STAGGERED}
            onClick={setReleaseMethodToStaggered}
            subheaderText="Unlock selected activity packs one at a time"
            tooltipText="The benefit of this approach is that students don’t feel overwhelmed with too many assignments and it requires them to complete the activity packs in order."
          />
          <ReleaseMethodOption
            descriptionText="Students will see all of the activity packs you have assigned to them at once. You can choose to assign all of the recommendations at once, or you can select some of the packs to assign now and then come back to this page to assign additional packs at a later date. "
            headerText="Immediate Release"
            illustration={releaseMethod === IMMEDIATE ? immediateReleaseIllustrationGreen : immediateReleaseIllustrationGray}
            isSelected={releaseMethod === IMMEDIATE}
            onClick={setReleaseMethodToImmediate}
            subheaderText="Make all selected activity packs available at once"
            tooltipText="The benefit of this approach is that students can see all of their assignments at once, and they can choose which activities they want to work on."
          />
        </div>
        <div className="footer">
          <p className="footer-note">{originalReleaseMethod ? 'Note: Your selection will be applied to the entire class' : ''}</p>
          <div className="buttons-wrapper">
            <button className="quill-button large outlined grey focus-on-light" onClick={handleClickCancel} type="button">Cancel</button>
            <DemoGuardedButton className={`quill-button large contained green focus-on-light ${releaseMethod ? '' : 'disabled'}`} disabled={!releaseMethod} onClick={handleClickAssign}>{originalReleaseMethod ? 'Save' : 'Assign'}</DemoGuardedButton>
          </div>
        </div>
      </div>
    </div>
  )

}

export default ReleaseMethodModal
